import { useRef, useEffect } from 'react';

function useScrollReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('animate-in');
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

export default function EquipmentRental() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useScrollReveal();
  const carouselRef = useScrollReveal(0.05);

  const equipment = [
    {
      id: 1,
      name: 'Surfboard',
      image: 'https://public.readdy.ai/ai/img_res/905c183a7203b9e322a319838e2ab97d.jpg',
      price: '15',
      availability: 'Available'
    },
    {
      id: 2,
      name: 'Wetsuit',
      image: 'https://readdy.ai/api/search-image?query=Modern%20wetsuit%20displayed%20on%20clean%20white%20background%20with%20natural%20shadow%2C%20professional%20black%20and%20teal%20neoprene%20surf%20wetsuit%2C%20high-quality%20product%20photography%20showing%20texture%20and%20design%20details%2C%20minimalist%20studio%20shot%20with%20soft%20lighting%20perfect%20for%20equipment%20rental%20catalog&width=600&height=800&seq=wetsuit-product-rental-002&orientation=portrait',
      price: '10',
      availability: 'Available'
    },
    {
      id: 3,
      name: 'Kitesurf Kit',
      image: 'https://readdy.ai/api/search-image?query=Complete%20kitesurfing%20equipment%20set%20on%20clean%20white%20background%20with%20natural%20shadow%2C%20colorful%20kite%20and%20board%20combo%20in%20vibrant%20turquoise%20and%20orange%20colors%2C%20high-quality%20product%20photography%20showing%20professional%20gear%2C%20minimalist%20studio%20shot%20with%20soft%20lighting%20perfect%20for%20equipment%20rental%20catalog&width=600&height=800&seq=kitesurf-kit-rental-003&orientation=portrait',
      price: '40',
      availability: 'Available'
    },
    {
      id: 4,
      name: 'SUP Board',
      image: 'https://readdy.ai/api/search-image?query=Stand-up%20paddleboard%20with%20paddle%20on%20clean%20white%20background%20with%20natural%20shadow%2C%20modern%20turquoise%20SUP%20board%20design%2C%20high-quality%20product%20photography%20showing%20sleek%20shape%20and%20paddle%2C%20minimalist%20studio%20shot%20with%20soft%20lighting%20perfect%20for%20equipment%20rental%20catalog&width=600&height=800&seq=sup-board-rental-004&orientation=portrait',
      price: '20',
      availability: 'Available'
    },
    {
      id: 5,
      name: 'Bodyboard',
      image: 'https://readdy.ai/api/search-image?query=Colorful%20bodyboard%20on%20clean%20white%20background%20with%20natural%20shadow%2C%20vibrant%20orange%20and%20blue%20boogie%20board%20design%2C%20high-quality%20product%20photography%20showing%20foam%20board%20texture%2C%20minimalist%20studio%20shot%20with%20soft%20lighting%20perfect%20for%20equipment%20rental%20catalog&width=600&height=800&seq=bodyboard-rental-005&orientation=portrait',
      price: '12',
      availability: 'Available'
    },
    {
      id: 6,
      name: 'Full Package',
      image: 'https://readdy.ai/api/search-image?query=Complete%20surf%20equipment%20package%20on%20clean%20white%20background%20with%20natural%20shadow%2C%20surfboard%20wetsuit%20and%20accessories%20bundle%20in%20coordinated%20turquoise%20and%20orange%20colors%2C%20high-quality%20product%20photography%20showing%20all%20gear%20together%2C%20minimalist%20studio%20shot%20with%20soft%20lighting%20perfect%20for%20equipment%20rental%20catalog&width=600&height=800&seq=full-package-rental-006&orientation=portrait',
      price: '35',
      availability: 'Available'
    }
  ];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction === 'left' ? -320 : 320, behavior: 'smooth' });
    }
  };

  return (
    <section id="rentals" className="py-24 bg-sand-50 relative overflow-hidden">
      <style>{`
        .reveal-section { opacity: 0; transform: translateY(30px); transition: opacity 0.75s ease, transform 0.75s ease; }
        .reveal-section.animate-in { opacity: 1; transform: translateY(0); }
        .reveal-section-delay { opacity: 0; transform: translateY(30px); transition: opacity 0.75s ease 0.18s, transform 0.75s ease 0.18s; }
        .reveal-section-delay.animate-in { opacity: 1; transform: translateY(0); }
      `}</style>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className="reveal-section flex items-end justify-between mb-12">
          <div>
            <p className="text-xs font-bold tracking-widest text-teal-600 uppercase mb-3">Location</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">Équipez-vous</h2>
            <p className="text-base text-gray-500">Matériel premium pour tous les niveaux</p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => scroll('left')} className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all cursor-pointer">
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button onClick={() => scroll('right')} className="w-11 h-11 flex items-center justify-center rounded-full border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all cursor-pointer">
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Equipment Cards */}
        <div ref={carouselRef} className="reveal-section-delay">
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {equipment.map((item) => (
              <div
                key={item.id}
                className="flex-shrink-0 w-72 bg-white border border-sand-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 snap-start cursor-pointer"
              >
                <div className="h-72 w-full bg-sand-100 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{item.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-2xl font-bold text-teal-600">€{item.price}</span>
                    <span className="text-sm text-gray-400">/jour</span>
                  </div>
                  <p className="text-xs text-green-600 font-medium mb-4">
                    <i className="ri-checkbox-circle-fill mr-1"></i>
                    {item.availability}
                  </p>
                  <button className="w-full bg-gray-900 text-white py-2.5 rounded-full font-semibold text-sm whitespace-nowrap hover:bg-teal-600 transition-all flex items-center justify-center gap-2 cursor-pointer">
                    Réserver
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Scroll Buttons */}
        <div className="flex md:hidden items-center justify-center gap-3 mt-6">
          <button onClick={() => scroll('left')} className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all cursor-pointer">
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button onClick={() => scroll('right')} className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all cursor-pointer">
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
